<template>
  <div class="wrapper">

    <div class="header-wrapper">
      <v-toolbar color="primary" dark max-height="56">
        <v-btn @click="routerBack" icon>
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-toolbar-title
          style="
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            margin: 0;
            padding: 0;
            max-width: 70%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
          class="text-center"
        >
          {{ childName }}
        </v-toolbar-title>

        <div style="width: 60px; margin-left: auto;">
          <v-btn
            icon
            @click="showDatePicker"
          >
            <v-icon large>mdi-calendar-filter</v-icon>
          </v-btn>
        </div>
      </v-toolbar>
    </div>

    <div v-if="fetchLoading" class="d-flex justify-center align-center fill-height pa-10">
      <vue-loading
        :active="fetchLoading"
        loader="bars"
        color="#EE6090"
        :size="{ width: '30px', height: '30px' }"
      ></vue-loading>
    </div>

    <div class="content-wrapper no-scroll-bar" v-else ref="scrollTarget">
      <v-list lines="two" density="compact">
        <!-- 1. key は template タグに移動し、v-list-item-group は削除 -->
        <v-list-item
          v-for="g in groupChats"
          :key="g.chatId"
          :value="g.chatId"
          link
          @click="toDetail(g.chatId)"
        >
            
            <!-- 2. 中央のテキスト: v-list-item-content は廃止 -->
            <!-- タイトル -->
            <v-list-item-title class="font-weight-bold subtitle-1">
              {{ displayTitle(g) }}
            </v-list-item-title>

            <!-- サブタイトル (日付) -->
            <!-- grey--text darken-5 は text-grey-darken-5 に変更 -->
            <v-list-item-subtitle class="subtitle-2 text-grey-darken-5">
              {{ dayFormat(g.createdAt) }}
            </v-list-item-subtitle>

            <!-- 3. 右側のアイコン: v-list-item-action は廃止 → append スロットを使用 -->
            <template v-slot:append>
              <div
                v-if="g.hasAttachment"
                class="badge d-flex justify-center align-center mr-2"
              >
                <v-icon>mdi-file-image</v-icon>
              </div>
            </template>

          </v-list-item>

          <v-divider />
      </v-list>

      <div
        class="d-flex justify-center pa-5"
        v-if="isFetchMoreAttachment"
      >
        <v-btn
          @click="fetchMoreAttachment"
          color="primary"
          text
          :loading="moreFetchLoading"
        >
          {{ t('home.loadMore', 'Load more') }}
        </v-btn>
      </div>
    </div>

    <div class="date-picker">
      <v-dialog v-model="showPicker" max-width="400" persistent>
        <v-card>
          <v-card-title class="text-center pt-4">{{ t('references.datePicker.title', 'Select year and month') }}</v-card-title>
    
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <div class="text-caption text-center mb-2">{{ t('references.datePicker.year', 'Year') }}</div>
                <v-list scrollable-y style="height: 250px">
                  <v-list-item
                    v-for="year in yearOptions"
                    :key="year"
                    :active="tempYear === year"
                    color="primary"
                    @click="tempYear = year"
                    class="text-center"
                  >
                    <v-list-item-title>{{ t('record.date.yearSuffix', '{year}', { year }) }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="6">
                <div class="text-caption text-center mb-2">{{ t('references.datePicker.month', 'Month') }}</div>
                <v-list scrollable-y style="height: 250px">
                  <v-list-item
                    v-for="month in 12"
                    :key="month"
                    :active="tempMonth === month"
                    color="primary"
                    @click="tempMonth = month"
                    class="text-center"
                  >
                    <v-list-item-title>{{ t('record.date.month', '{month}', { month }) }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="showPicker = false">{{ t('common.cancel', 'Cancel') }}</v-btn>
            <v-btn variant="text" color="primary" @click="dateSelected">{{ t('references.datePicker.apply', 'Apply') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

  </div>
</template>
<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/en';
import { t as translate, locale as appLocale } from '@/i18n';
dayjs.locale(appLocale === 'ja' ? 'ja' : 'en');
import { formatDisplayDate } from '@/utils/dateFormat';

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

import { attachmentModule } from '@/store/entityModules/attachment';
import { resourceModule } from '@/store/entityModules/resource';
import { chatModule } from '@/store/entityModules/chat';

import {
  HomeAttachmentResponse,
} from '@API/src/component/home/attachment/types';

interface DataType {
  moreCnt: number;
  tempYear: number;
  tempMonth: number;
  yearOptions: number[];
  childId: number;
  childName: string;
  fetchLoading: boolean;
  moreFetchLoading: boolean;
  isFetchMoreAttachment: boolean;
  isAttachmentUrls: boolean;
  imgLoading: boolean;
  showingUrls: string[];
  showPicker: boolean;
  selectedDate: string;
}

export default defineComponent({
  components: {
    'vue-loading': Loading,
  },

  data: (): DataType =>({
    moreCnt: 0,
    tempYear: new Date().getFullYear(),
    tempMonth: new Date().getMonth() + 1,
    // 年の選択肢（例：前後5年分など）
    yearOptions: Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i),
    childId: NaN,
    childName: '',
    fetchLoading: true,
    moreFetchLoading: false,
    isFetchMoreAttachment: true,
    imgLoading: false,
    isAttachmentUrls: false,
    showingUrls: [],
    showPicker: false,
    selectedDate: localStorage.selectedDate,
  }),

  computed: {
    groupChats() {
      return chatModule.groupChats;
    },
    groupChatsTotal() {
      return chatModule.groupChatsCount;
    },
    // 詳細画面で翻訳した内容（localStorage）を読み込む
    translatedChats(): Record<number, string> {
      if (!this.childId || Number.isNaN(this.childId)) return {};
      try {
        const raw = localStorage.getItem(this.getTranslatedChatsKey());
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    },
  },

  methods: {
    getTranslatedChatsKey(): string {
      return `translatedChats_${this.childId}`;
    },
    // 翻訳済みデータがある場合は翻訳後のタイトル（1行目）を表示する
    displayTitle(chat: { chatId: number; title: string | null }): string {
      const translated = this.translatedChats[chat.chatId];
      if (!translated) return chat.title ?? '';
      const idx = translated.indexOf('\n');
      return idx === -1 ? translated : translated.slice(0, idx);
    },
    t(path: string, fallback = path, params: Record<string, string | number> = {}) {
      return translate(path, fallback, params);
    },
    // ダイアログを開くときに今の値をセット
    openPicker() {
      const d = new Date(this.selectedDate);
      this.tempYear = d.getFullYear();
      this.tempMonth = d.getMonth() + 1;
      this.showPicker = true;
    },
    routerBack() {
//      localStorage.removeItem("selectedDate");
      this.$router.replace('/record');
    },
    toDetail(chatId: number) {
      const el = this.$refs.scrollTarget as HTMLElement;
      const scrollY = el?.scrollTop ?? 0;
      const moreCnt = this.moreCnt;

      this.$router.push({
        path: "/references/detail",
        query: {
          chatId: String(chatId),
          childId: String(this.childId),
          childName: this.childName,
        },
        state: { scrollY, moreCnt }
      })
    },

    dayFormat(date: string) {
      return formatDisplayDate(date);
    },

    async fetchGroupChats() {
      this.fetchLoading = true;
      chatModule.removeGroupChats();
      await chatModule.fetchGroupChats({
        childId: this.childId,
        limit: 10,
        skip: 0,
        date: this.selectedDate,
      });
      if (this.groupChats.length >= this.groupChatsTotal) {
        this.isFetchMoreAttachment = false;
      }
      this.fetchLoading = false;
    },

    async fetchMoreAttachment() {
      this.moreCnt++;
      await this.fetchMoreAttachmentMain();
    },

    async fetchMoreAttachmentMain() {
      this.moreFetchLoading = true;
      await chatModule.fetchGroupChats({
        childId: this.childId,
        limit: 10,
        skip: this.groupChats.length,
        date: this.selectedDate,
      });
      if (this.groupChats.length >= this.groupChatsTotal) {
        this.isFetchMoreAttachment = false;
      }
      this.moreFetchLoading = false;
    },

    insertStr(str, index, insert) {
      return str.slice(0, index) + insert + str.slice(index, str.length);
    },
    formatAmzIso(dateStr: string): string {
      let formatted = dateStr;
      formatted = this.insertStr(formatted, 4, '-');
      formatted = this.insertStr(formatted, 7, '-');
      formatted = this.insertStr(formatted, 13, ':');
      formatted = this.insertStr(formatted, 16, ':');
      return formatted;
    },
    isPresignedUrlExpired(url: string): boolean {
      try {
        const arr = url.split(/[=&]/);
        if (arr.length >= 12) {
          const XAmzDate = dayjs(this.formatAmzIso(arr[5])).locale('ja');
          const XAmzExpires = Number(arr[7]);
          const ExpirationTime = dayjs(XAmzDate).add(XAmzExpires, 'seconds');
          const Now = dayjs();
          const isExpired = Now.isAfter(ExpirationTime);
          return isExpired;
        } else {
          return true;
        }
      } catch (error) {
        return true;
      }
    },
    async openImg(attachment: HomeAttachmentResponse) {
      let img = attachment.attachmentUrl;
      const isImgExpired = this.isPresignedUrlExpired(img);
      if (isImgExpired) {
        this.imgLoading = true;
        const objectKey = attachment.objectKey;
        const imgContent = await resourceModule.postResourceDownload({
          objectKey,
        });
        img = imgContent.preSignedUrl;
        this.imgLoading = false;
      }
      const ua = navigator.userAgent;
      const isIOS = ua.indexOf('iPhone') >= 0
        || ua.indexOf('iPad') >= 0
        || Number(navigator.userAgent.indexOf('iPod')) >= 0;
      if (isIOS) {
        if ((window as any).webkit.messageHandlers) {
          (window as any).webkit.messageHandlers.pdf.postMessage(`${[img]}`);
        }
      } else {
        this.isAttachmentUrls = true;
        this.showingUrls = [img];
      }
    },
    closeChatImg() {
      this.isAttachmentUrls = false;
      this.showingUrls = [];
    },
    showDatePicker() {
      this.showPicker = true;
    },
    dateSelected() {
      const date = new Date(this.tempYear, this.tempMonth - 1, 1);
      this.showPicker = false;
      localStorage.selectedDate = dayjs(date).format('YYYY-MM');
      sessionStorage.setItem('referenceDate', dayjs(date).format('YYYY-MM'));
      this.selectedDate = localStorage.selectedDate;

      if (typeof this.$route.query.childId === 'string') {
        this.childId = Number(this.$route.query.childId);
        this.fetchGroupChats();
      } else {
        this.routerBack();
      }

      this.showPicker = false;
    },
  },

  async mounted() {
    if (typeof this.$route.query.childName === 'string') {
      this.childName = this.$route.query.childName;
    }
    const referenceDate = sessionStorage.getItem('referenceDate');
    if (referenceDate) {
      localStorage.selectedDate = referenceDate;
      this.selectedDate = localStorage.selectedDate;
    }
    if (typeof this.$route.query.childId === 'string') {
      this.childId = Number(this.$route.query.childId);
      await this.fetchGroupChats();
    } else {
      this.routerBack();
    }

    if (localStorage.selectedDate) {
      this.selectedDate = localStorage.selectedDate;
    }

    this.moreCnt = window.history.state?.moreCnt ? Number(window.history.state?.moreCnt) : 0;
    for (let i = 0; i < this.moreCnt; i++) {
      await this.fetchMoreAttachmentMain();
    }

    // ブラウザの戻る操作などで state に値があるか確認
    const savedScrollY = window.history.state?.scrollY;

    if (savedScrollY) {
      // DOMの描画（リストのレンダリング）が終わるのを待ってからスクロール
      await nextTick();
      const el = this.$refs.scrollTarget as HTMLElement | undefined;
      if (el) {
        el.scrollTop = savedScrollY;
      }
    }
  },
  
});
</script>
<style lang="scss" scoped>
.wrapper {
  height: 100%; /* Fallback */
  overflow-y: scroll;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
    -webkit-appearance: none;
  }
  @supports (bottom: env(safe-area-inset-bottom)) {
    padding-top: env(safe-area-inset-top);
  }

  .header-wrapper {
    height: 56px;
    width: 100%;
    position: fixed;
    z-index: 3;

    .date_card {
      background-color: #ffffff;
    }
    .outline {
      border: 1px solid rgb(var(--v-theme-primary));
    }
  }
  .grey-border {
    height: 2px;
    width: 100%;
    background-color: #F5F6F6;
  }

  .content-wrapper {
    height: calc(calc(var(--vh, 1vh) * 100) - 56px - 56px);
    margin: 64px 0 56px;
    //overflow-y: hidden;
    overflow-y: scroll;

    // padding: 122px 0 48px;
    // height: 100%;
    // overflow-y: scroll;
    // -ms-overflow-style: none;
    // scrollbar-width: none;
    // &::-webkit-scrollbar {
    //   display: none;
    //   -webkit-appearance: none;
    // }
  }

  .date-picker {
    position: absolute;
    top: 57px;
    right: 0;
    z-index: 99;
    border-radius: 4px;
    box-shadow: 0 5px 5px -3px rgba(0, 0, 0, .2), 0 8px 10px 1px rgba(0, 0, 0, .14), 0 3px 14px 2px rgba(0, 0, 0, .12);
  }
}
.centered-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
}
</style>
